import React, { useState, useCallback } from "react";
import Step1 from "./Step1";
import Step2 from "./Step2";
import { useLocation, useNavigate } from "react-router-dom";

const nvl = (v) => (v == null || v === "" ? null : v); // null-ish normalize
const str = (v) => (v == null ? "" : String(v)); // string normalize

const ManualMarketingAI = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // ✅ state 형태 통일
    const raw = location.state ?? {};
    const incoming = raw?.data ?? raw;
    const incomingIsRegen = Boolean(raw?.isRegen ?? incoming?.isRegen);

    const headerSelectedPath = incoming?.headerSelectedPath ?? null;
    const headerSelectedPrompt = incoming?.headerSelectedPrompt ?? "";

    const userMenu = localStorage.getItem("customMenu") || "";

    // ✅ AI 추천값: 없으면 null
    const aiAge = nvl(localStorage.getItem("aiAge"));
    const aiChannel = nvl(localStorage.getItem("aiChannel"));
    const aiTitle = nvl(localStorage.getItem("aiTitle"));
    const aiStyle = nvl(localStorage.getItem("aiStyle"));

    // ✅ 재생성은 Step2부터
    const [step, setStep] = useState(incomingIsRegen ? 2 : 1);

    const [formData, setFormData] = useState(() => {
        // ✅ nullable(없음) 필드들은 null로 통일
        const base = {
            category: "",
            age: "",
            channel: "",
            subChannel: "",
            theme: "",
            title: "",
            customMenu: "",
            customText: "",

            // ai* 값은 "없음"이 기본이므로 null이 안전
            aiAge: null,
            aiChannel: null,
            aiTitle: null,
            aiStyle: null,

            // prompt는 빈 문자열도 가능하지만, 추천 채우기 로직과 섞이면 헷갈리니 일단 "" 유지
            prompt: "",

            // ✅ 이건 반드시 null이어야 함 (""면 Step2가 '있다'고 착각함)
            aiThumbnail: null,

            style: "", // 선택값은 ""로 ok
        };

        // ✅ result/regen에서 넘어온 값 seed
        const seeded = {
            age: str(incoming?.age),
            channel: str(incoming?.channel),
            subChannel: str(incoming?.subChannel),
            theme: str(incoming?.theme),
            title: str(incoming?.title),
            style: str(incoming?.style),
            customText: str(incoming?.customText),

            // ✅ ai* 는 null 유지가 안전
            aiAge: nvl(incoming?.aiAge),
            aiChannel: nvl(incoming?.aiChannel),
            aiTitle: nvl(incoming?.aiTitle),
            aiStyle: nvl(incoming?.aiStyle),

            // prompt는 string
            prompt: str(incoming?.prompt),

            // ✅ 썸네일은 null/문자열 경로만 허용. "" 금지
            aiThumbnail: nvl(incoming?.aiThumbnail),
        };

        // ✅ 헤더에서 이미지 선택해서 들어오는 경로는 prompt/thumbnail만 덮기
        if (headerSelectedPath) {
            return {
                ...base,
                ...seeded,
                aiThumbnail: nvl(headerSelectedPath) ?? seeded.aiThumbnail,
                prompt: headerSelectedPrompt || seeded.prompt,
            };
        }

        return { ...base, ...seeded };
    });

    // ✅ undefined만 무시하고 merge (null은 "비움" 의도니까 유지)
    const updateFormData = useCallback((newData) => {
        if (!newData) return;
        setFormData((prev) => {
            const patch = { ...newData };
            Object.keys(patch).forEach((k) => {
                if (patch[k] === undefined) delete patch[k];
            });
            return { ...prev, ...patch };
        });
    }, []);

    const goNext = () => setStep((prev) => Math.min(prev + 1, 5));
    const goBack = () => setStep((prev) => Math.max(prev - 1, 1));

    const handleNewAdCreation = (patchFromStep2) => {
        const merged = { ...formData, ...patchFromStep2 };

        const manualData = {
            ...merged,
            prompt: merged.prompt ?? "",

            store_name: incoming?.store_name,
            road_name: incoming?.road_name,
            district_name: incoming?.district_name,
            detail_category_name: incoming?.detail_category_name,
            store_business_number: incoming?.store_business_number,
            main: incoming?.main,
            temp: incoming?.temp,
            category: incoming?.category || merged?.customMenu || userMenu,
        };

        console.log("[ManualMarketingAI] -> loading manualData:", manualData);
        navigate(`/manual/marketing/ai/loading`, { state: manualData });
    };

    const navHome = () => {
        navigate("/ads/main", {
            state: { storeBusinessNumber: incoming?.store_business_number },
        });
    };

    return (
        <div className="w-full h-screen bg-white">
            {step === 1 && (
                <Step1
                    data={formData}
                    userMenu={userMenu}
                    updateData={updateFormData}
                    onNext={goNext}
                    onHome={navHome}
                />
            )}

            {step === 2 && (
                <Step2
                    data={formData}
                    updateData={updateFormData}
                    onBack={goBack}
                    onHome={navHome}
                    onNext={handleNewAdCreation}
                    headerSelectedPath={headerSelectedPath}
                    isRegen={incomingIsRegen}
                    exData={incomingIsRegen ? incoming : undefined}
                    aiAge={aiAge}
                    aiChannel={aiChannel}
                    aiTitle={aiTitle}
                    aiStyle={aiStyle}
                />
            )}
        </div>
    );
};

export default ManualMarketingAI;
